import { GREEN } from '../Theme/palette';
import { PlusOutlined, UploadOutlined } from '@ant-design/icons';
import { useAddToPurchaseOrder, useInventory, useInventoryCategories, usePageTitle, useView } from '@hooks';
import { L } from '@lib';
import { Inventory } from '@services';
import { useStore } from '@stores';
import { Button, Space } from 'antd';
import { observer } from 'mobx-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  AddToPurchaseOrderModal,
  InventoryImportDrawer,
  InventoryImportDrawerHandle,
  InventoryItemDrawer,
  InventoryItemDrawerHandle,
  InventoryReturnModal,
  InventoryUploadDrawer,
  InventoryUploadDrawerHandle,
} from '@components';
import Presentation from './InventoryTable.presentation';

interface IProps {
  embedded?: boolean;
}

export const InventoryTable: React.FC<IProps> = observer(({ embedded }) => {
  const { isMobile } = useView();
  const inventoryHook = useInventory();
  const { options: categoryOptions } = useInventoryCategories();
  const { inventoryStore } = useStore();
  const { setHeaderActions } = usePageTitle();
  const { addFromInventory } = useAddToPurchaseOrder();

  const itemDrawerRef = useRef<InventoryItemDrawerHandle>(null);
  const uploadDrawerRef = useRef<InventoryUploadDrawerHandle>(null);
  const importDrawerRef = useRef<InventoryImportDrawerHandle>(null);
  const [returnItem, setReturnItem] = useState<Inventory | null>(null);
  const [pendingPOItem, setPendingPOItem] = useState<Inventory | null>(null);
  const [poModalLoading, setPoModalLoading] = useState(false);

  const openUpload = useCallback(
    (prefill?: { items?: any[]; supplierId?: string }) => uploadDrawerRef.current?.open(prefill),
    []
  );

  useEffect(() => {
    if (embedded) return;
    setHeaderActions(headerButtons);
    return () => setHeaderActions(null);
  }, [isMobile, openUpload, embedded]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleEdit = (record: Inventory) => itemDrawerRef.current?.edit(record);

  const handleDuplicate = (record: Inventory) =>
    openUpload({
      items: [
        {
          name: record.name,
          reference: record.reference,
          partNumber: record.partNumber,
          description: record.description,
          quantity: 1,
          costPrice: record.costPrice,
          sellPrice: record.sellPrice,
          categoryId: record.categoryId,
          isInShop: record.isInShop,
          location: record.location,
        },
      ],
      supplierId: record.supplierId,
    });

  const handleDelete = async (id: string) => {
    const deleted = await inventoryStore.delete({ id });
    if (deleted) inventoryHook.refresh();
    return deleted;
  };

  const headerButtons = (
    <Space>
      <Button
        type="primary"
        icon={<UploadOutlined />}
        onClick={() => importDrawerRef.current?.open()}
      >
        {!isMobile ? L('Import Excel') : null}
      </Button>
      <Button
        icon={<PlusOutlined />}
        onClick={() => openUpload()}
        style={{ backgroundColor: GREEN.badge, borderColor: GREEN.badge, color: '#fff' }}
      >
        {!isMobile ? L('New Inventory') : null}
      </Button>
    </Space>
  );

  return (
    <>
      {embedded && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
          {headerButtons}
        </div>
      )}
      <Presentation
        inventoryHook={inventoryHook}
        categoryOptions={categoryOptions}
        onEdit={handleEdit}
        onDuplicate={handleDuplicate}
        onReturnToSupplier={setReturnItem}
        onAddToPurchaseOrder={setPendingPOItem}
      />
      <AddToPurchaseOrderModal
        open={!!pendingPOItem}
        part={{
          name: pendingPOItem?.name ?? '',
          number: pendingPOItem?.partNumber,
          reference: pendingPOItem?.reference,
        }}
        context={{ supplierName: pendingPOItem?.supplierName }}
        initialQty={1}
        loading={poModalLoading}
        onConfirm={async (qty) => {
          if (!pendingPOItem) return;
          setPoModalLoading(true);
          try {
            await addFromInventory(pendingPOItem, qty);
          } finally {
            setPoModalLoading(false);
            setPendingPOItem(null);
          }
        }}
        onCancel={() => setPendingPOItem(null)}
      />
      <InventoryReturnModal
        open={!!returnItem}
        item={returnItem}
        onClose={() => setReturnItem(null)}
        onConfirmed={inventoryHook.refresh}
      />
      <InventoryItemDrawer
        ref={itemDrawerRef}
        categoryOptions={categoryOptions}
        onSuccess={inventoryHook.refresh}
        onDelete={handleDelete}
      />
      <InventoryUploadDrawer
        ref={uploadDrawerRef}
        categoryOptions={categoryOptions}
        onSuccess={() => inventoryHook.refresh()}
      />
      <InventoryImportDrawer ref={importDrawerRef} onSuccess={() => inventoryHook.refresh()} />
    </>
  );
});
